// eslint-disable-next-line no-unused-vars
import React from 'react';
import { Link } from 'react-router-dom';
import { bool, object, func, string } from 'prop-types';
import styled from '@emotion/styled';
import { Button, Loading } from '@carbon/react';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useContextSelector } from 'use-context-selector';
import { Package, Copy, Megaphone, SquareStack, PackageOpen, Trash, Eye } from 'lucide-react';

import { StyledOverlay } from 'Components/Products/Products';
import { renderStock } from 'Components/Products/helpers';
import Img from 'Containers/Image/Image';
import useNumberUtils from 'Hooks/useNumberUtils';
import { UserContext } from 'Contexts/UserContext';
import ProductPropType from 'Types/product';
import Price from 'Components/Price/Price';
import Badges from 'Components/Badges/Badges';
import AddToCart from 'Components/AddToCart/AddToCart';
import ProductPriceRequest from 'Containers/Products/ProductPriceRequest';
import { StyledProductListItem } from 'Components/Products/StyledProductItems';
import { StyledAddToWishlist } from 'Components/Products/ProductWishlistItem';
import { stockAlertWishlistId } from 'Contexts/WishlistContext';
import useToastNotifications from 'Hooks/useToastNotifications';
import copyToClipboard from 'Utils/copyToClipboard';
import DeliveryEstimate from 'Components/Product/DeliveryEstimate';

const StyledProductWishlistItem = styled(StyledProductListItem)`
  .details {
    overflow: visible;

    .title {
      &:hover {
        color: ${ props => props.theme.colors.blue };
      }
    }
  }

  .previewContainer {
    display: flex;
    margin-bottom: 20px;

    .preview {
      .image {
        max-height: 180px;
      }
    }

    .title {
      font-size: 18px;
      display: none;
      color: #3A3A3A;
      margin-top: 5px;
    }
  }

  .overlay {
    height: 90%;
    justify-content: center;
    align-items: center;

    .cds--loading {
      width: 2rem;
      height: 2rem;
    }
  }

  .priceNotApplicable {
    font-size: 1.5em;
    line-height: 1.25em;
    color: #D52B1E;
  }

  .actionButtons {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    .addToCart,
    .stockAlert {
      //&:not(:disabled) {
      //  border: 1px solid ${ props => props.theme.colors.default };
      //}
      svg {
        margin-right: 12px;
      }
      max-width: 200px;
      width: 100%;
      font-size: 0.875rem !important;
    }

    .wishlistActions {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-end;
      margin-top: 5px;
      max-width: 200px;
      width: 100%;

      .cds--dropdown__wrapper {
        flex-grow: 1;
      }

      .delete {
        width: 40px;
        height: 40px;
        padding: 0;
        min-height: 0;
        min-width: 0;
        border: 1px solid #FEE7E5;
        background-color: #FCF3F1;
        color: ${ props => props.theme.colors.darkGray };
        outline: 0;
        box-shadow: none;
        text-align: center;

        svg {
          margin: 0 auto;
          fill: none !important;
          stroke: currentColor !important;
        }
      }
    }
  }

  @media screen and (max-width: 1024px) {
    flex-direction: column;
    margin-bottom: 20px;
    border: 1px solid #e2e2e2;
    padding: 10px;

    .previewContainer {
      .preview {
        top: 0;
        max-width: 75px;
        min-width: 75px;
        max-height: 75px;
        min-height: 75px;
        height: 75px;

        .image {
          max-height: 75px;
        }

        .badges .badge {
          padding: 0px 3px;
          font-size: 9px;
        }
      }

      .title {
        display: inline;
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;

        &:hover {
          color: ${ props => props.theme.colors.blue };
        }
      }
    }

  
    .details {
      width: 100%;
      flex-direction: column;

      .title {
        display: none;
      }

      .helperButtons {
        button {
          display: none;
        }

        .resealed {
          font-weight: normal;
          stroke: #498246;
        }
      }

      .priceContainer {
        width: 100%;
        border-left: none;
        align-items: start;
        padding-left: 0;
        min-width: auto;

        .price {
          align-items: start;
        }

        .actionButtons {
          align-items: start;

          .productPriceRequest {
            width: 100%;

            button {
              max-width: none;
            }
          }

          .stockAlert {
            max-width: none;
          }

          .addToCartContainer {
            width: 100%;

            .addToCart {
              max-width: none;
              flex-grow: 1;
            }
          }

          .wishlistActions {
            max-width: none;

            > div {
              max-width: none;
              flex-grow: 1;
            }
          }
        }
      }
    }
  }
`;

const ProductDetailWishlistItem = (
  {
    product,
    wishlist,
    onStockAlertModalOpen,
    onQuickView,
    loading,
    addToCartLoading,
    onMoveToWishlist,
    onAddToCart,
    onDelete,
    onChangeQuantity,
    defaultCurrency,
    withVat
  }
) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatNumber } = useNumberUtils();
  const currency = useContextSelector(UserContext, ({ data }) => data?.defaultCurrency);
  const toast = useToastNotifications();

  const copyCodeToClipboard = async code => {
    const resp = await copyToClipboard(code);

    if (resp) {
      toast.addNotification({
        type: 'success',
        title: t('products.copyCodeSuccess')
      });
    }
  };

  return (
    <StyledProductWishlistItem
      theme={ theme }
      canAddToCart={ product.stockValue >= product.minQuantity }
      className="productListItem"
    >
      <StyledOverlay className="overlay" open={ loading }>
        { loading && <Loading active={ loading } small withOverlay={ false } /> }
      </StyledOverlay>
      <div className="previewContainer">
        <div className="preview">
          { !product?.hasPriceRequest && !product?.warnings?.blocked_prices && (
            <Badges
              isEol={ product.isEol }
              isOnDemandOnly={
                // eslint-disable-next-line no-nested-ternary
                product.reservedStockValue > 0
                  ? false
                  : product.stockValue <= 0
                    ? product.isOnDemandOnly
                    : false
              }
              isResealed={ !!product.originalCode }
              isNew={ product.isNew }
              savings={ Math.round(((product.price - product.promoPrice) / product.price) * 100) / 100 }
              isSupplierOffer={ !product?.hasPrice }
              hasSupplierOffers={ product?.hasSupplierOffers }
            />
          ) }
          <Link
            to={ `/products/${product.slug}/pd/${product.id}` }
            state={ {
              component: 'product_detail_wishlist',
              source: 'wishlist',
              clickedOn: 'image',
              productId: product.id
            } }
          >
            <Img
              className="image"
              src={ [
                product.picture,
                product?.pictures?.at(0)?.urlThumbnailPicture,
              ] }
              unloader={ <Package size={ 100 } strokeWidth={ 1.25 } /> }
              loading="lazy"
              alt={ product.name }
            />
          </Link>
        </div>
        <Link
          className="title"
          to={ `/products/${ product.slug }/pd/${product.id}` }
          source={ {
            component: 'product_detail_wishlist',
            source: 'wishlist',
            clickedOn: 'title',
            productId: product.id
          } }
        >
          { product.title }
        </Link>
      </div>
      <div className="details">
        <div>
          <Link
            className="title"
            to={ `/products/${ product.slug }/pd/${product.id}` }
            state={ {
              component: 'product_detail_wishlist',
              source: 'wishlist',
              clickedOn: 'title',
              productId: product.id
            } }
          >
            { product.title }
          </Link>
          <p>
            { t('products.code') }:
            <span>{ product.code }</span>
            <span><Copy size={ 18 } className="copy" onClick={ () => copyCodeToClipboard(product?.code || '') } /></span>
          </p>
          <p className="vendor">
            { t('products.vendor') }:
            <span>{ product.manufacturer }</span>
          </p>
          <p className="warranty">
            { t('products.warranty') }:
            <span>{ product.warranty }</span>
          </p>
          { !product?.warnings?.blocked_stocks && renderStock({ product, t }) }
          { (!product?.hasPrice || product?.hasSupplierOffers) && (
            <DeliveryEstimate
              estimate={ product?.supplierOfferDeliveryTimeDays }
              isExternalSupplier={ !product?.hasPrice }
            />
          ) }
          <div className="helperButtons">
            <Button
              onClick={ onQuickView }
              kind="primary"
              className="green"
              data-test="onQuickView"
              data-testid={ product.productId }
            >
              <Eye />
              { t('products.quickView') }
            </Button>
            { (product?.resealedCount > 0) && (
              <p className="resealed">
                <PackageOpen /> { product?.resealedCount || '' } { t('products.resealed') }
              </p>
            ) }
          </div>
        </div>
        <div className="priceContainer">
          { (!product?.hasPriceRequest && !product?.warnings?.blocked_prices) && (
          <Badges
            specialPriceValidTo={ product.specialPriceValidTo }
            specialPrice={
              product.price !== product.promoPrice
              && ((product.price - product.promoPrice) / product.price) >= 0.01
            }
            fixedPrice={ product.fixedPrice }
            isClearance={ product.stockClearingPrice }
          />
          ) }
          { (!product?.hasPriceRequest && !product?.warnings?.blocked_prices) ? (
            <Price
              price={ product.price }
              promoPrice={ product.promoPrice }
              withTooltip
              withVat={ withVat }
              displayVatInfo
              tooltipContent={ `
                ${ t('products.greenStamp') }
                ${ formatNumber(product.greenStampValue, { currency }) }
            ` }
            />
          ) : null }
          { product.minQuantity > 1 && (
            <p className="minQuantity">
              <SquareStack />
              { t('products.multipleOf', { multiple: product.minQuantity }) }
            </p>
          ) }
          <div className="actionButtons">
            { product?.hasPriceRequest && <ProductPriceRequest productId={ product.id } /> }
            { (
              (!product.stockAvailable || !product.stockValue)
              && !Object.keys(product?.warnings || {}).length > 0
              && wishlist.id !== stockAlertWishlistId
              && !product?.hasPriceRequest
            ) && (
              <Button
                data-test="onStockAlert"
                data-testid={ product.productId }
                className="red stockAlert"
                kind="danger--ghost"
                onClick={ onStockAlertModalOpen }
              >
                <Megaphone />
                { t('products.moveToStockAlert') }
              </Button>
            ) }
            { ((
              (product.stockValue > 0 && wishlist.id !== stockAlertWishlistId)
              || Object.keys(product?.warnings || {}).length > 0)
              && !product?.hasPriceRequest
            ) && (
              <AddToCart
                className="addToCart"
                onClick={ () => onAddToCart(defaultCurrency, withVat) }
                trackingContext={ {
                  location: 'product_details_wishlist',
                  product_sku: product?.code,
                } }
                onChange={ onChangeQuantity }
                disabled={
                  !product.priceAvailable
                  || addToCartLoading
                  || product.warnings?.blocked_prices
                  || product.warnings?.blocked_stocks
                  || product.warnings?.blocked_orders
                }
                showSelector={ !(product.warnings?.blocked_prices || product.warnings?.blocked_stocks || product.warnings?.blocked_orders) }
                loading={ addToCartLoading }
                min={ Math.max(product.minQuantity, 1) }
                step={ Math.max(product.minQuantity, 1) }
                max={ 100 }
                value={ product.quantity }
                id={ product.id }
                size="sm"
              />
            ) }
            <div className="wishlistActions">
              <StyledAddToWishlist
                action="move"
                data-test="wishlistDetailedItemMove"
                data-testid={ product.id }
                wishlist={ wishlist }
                onAddToWishlist={ onMoveToWishlist }
                product={ product }
                label={ t('wishlists.moveToList') }
              />
              <Button
                className="delete"
                kind="ghost"
                renderIcon={ Trash }
                iconDescription={ t('common.delete') }
                onClick={ onDelete }
                data-test="wishlistDetailedItemDelete"
                data-testid={ product.id }
              />
            </div>
          </div>
        </div>
      </div>
    </StyledProductWishlistItem>
  );
};

ProductDetailWishlistItem.propTypes = {
  product: ProductPropType,
  wishlist: object,
  onAddToCart: func,
  onQuickView: func,
  onChangeQuantity: func,
  loading: bool,
  onStockAlertModalOpen: func,
  addToCartLoading: bool,
  onMoveToWishlist: func,
  onDelete: func,
  defaultCurrency: string,
  withVat: bool
};
ProductDetailWishlistItem.displayName = 'ProductDetailWishlistItemComponent';

export default ProductDetailWishlistItem;
